import { Injectable } from '@angular/core';
import { Map } from 'immutable';
import {
  ResourceIdentifier,
  deserialiseOne,
  jsonapiIdentifier,
  toJsonApi,
} from 'jsonapi-transformers';
import {
  Observable,
  concat,
  from as observableFrom,
  of as observableOf,
  throwError as observableThrowError,
} from 'rxjs';
import { catchError, map, mergeMap, scan, skip } from 'rxjs/operators';

import { CreateListPayload } from '®app/reading-lists/list/create/list-create.actions';
import {
  getPageOffsets,
  getPageableItemsErrorWithOffset,
  getTotalPages,
} from '®shared/list-item/store/pageable-items.utils';
import { getRelationshipFromNodeAndOverride } from '®shared/reading-list/hierarchy/common/hierarchy-and-student-numbers.service';
import { TarlV3Api } from '®shared/tarl-v3-api';
import { MetaData, TarlV3Jsonapi } from '®shared/tarl-v3-jsonapi';
import { PageOf } from '®shared/tarl-v3-jsonapi/jsonapi-utils';
import { UserRecord } from '®shared/user';

import { Citation } from './citation';
import { ListDetailsFormModel } from './list-details-form';
import { ListItem } from './list-item';
import {
  LIST_ITEMS_PAGE_SIZE,
  PAGEABLE_EMPTY_ITEMS,
  PageableItems,
} from './pageable-items';
import { ReadingListDetails } from './reading-list-details';
import { Section } from './section';

export type CitationsByItem = Map<string, Citation>;

const CONCURRENCY_LIMIT = 10;

const LIST_ITEM_INCLUDES = ['content', 'importance', 'resource.part_of'];

const SECTION_ITEM_INCLUDES = [
  'content',
  'importance',
  'resource.part_of',
  'list',
];

@Injectable()
export class ReadingListService {
  constructor(
    private tarlV3Api: TarlV3Api,
    private tarlV3Jsonapi: TarlV3Jsonapi,
  ) {}

  updateListOwners(
    listId: string,
    users: UserRecord[],
  ): Observable<MetaData<ResourceIdentifier[]>> {
    const path = this.tarlV3Jsonapi.path(
      '/lists/{listId}/relationships/owners',
      { listId },
    );

    const userResourceIdentifiers = users.map(jsonapiIdentifier);

    return this.tarlV3Jsonapi.patchToManyRelationship(
      path,
      userResourceIdentifiers,
    );
  }

  /**
   * Get a reading list from the API.
   *
   * @param {string} listId - The ID of the list to retrieve.
   * @param {boolean=} isDraft - Whether or not to fetch the draft copy
   * @return {Observable<ReadingListDetails>} - The current reading list details
   **/
  getReadingListDetails(
    listId: string,
    isDraft = false,
  ): Observable<ReadingListDetails> {
    const path = this.tarlV3Jsonapi.path('/lists/{listId}', { listId });

    // JSON:API includes to request list structure be included in response
    const include = [
      'license',
      'nodes',
      'owners',
      'period',
      'rolled_over_to',
      'rolled_over_from',
      'sections_recursively',
      'tenant.bookstores',
      'tenant.citation_styles',
      'tenant.importances',
      'tenant.licenses',
      'tenant.periods',
      'tenant.reports',
    ];

    if (isDraft) {
      include.push('list_publish_action');
    }

    const params = { include };
    const additionalParams = this.getAdditionalQueryParams(isDraft);
    return this.tarlV3Jsonapi.getResource<ReadingListDetails>(
      path,
      params,
      additionalParams,
    );
  }

  /**
   * Update reading list details using the API.
   *
   * This will patch the title, description, time period, license and privacy details.
   *
   * @param {ReadingListDetails} listDetails - The pre-edited list.
   * @param {ListDetailsFormModel} listDetailsForm - Contains the details to update.
   * @returns {Observable<ReadingListDetails>} - The updated reading list details
   */
  updateReadingListDetails(
    listId: string,
    listDetailsForm: ListDetailsFormModel,
  ): Observable<ReadingListDetails> {
    const patchedListDetails = Object.assign(
      listDetailsForm.toReadingListDetails(),
      { id: listId },
    );
    const path = this.tarlV3Jsonapi.path('/lists/{listId}', {
      listId,
    });

    // Always return the draft version of the list to ensure we get the updated ETag
    const returnDraft = true;
    const additionalParams = this.getAdditionalQueryParams(returnDraft);

    return this.tarlV3Jsonapi.patchResource<ReadingListDetails>(
      path,
      patchedListDetails,
      {},
      additionalParams,
    );
  }

  /**
   * Fetch a single list item.
   *
   * @param  {string} itemId - the ID for the item to fetch
   * @param  {boolean=} isDraft - whether to fetch the draft item
   * @return {Observable<ListItem>} The list item, deserialised from JSON:API
   */
  getItem(itemId: string, isDraft = false): Observable<ListItem> {
    const path = this.tarlV3Jsonapi.path('/items/{itemId}', { itemId });

    // JSON:API includes to request list structure be included in response
    const include = [
      'content',
      'importance',
      'list',
      'resource.part_of',
      'tenant.bookstores',
      'tenant.citation_styles',
      'tenant.importances',
      'tenant.licenses',
      'tenant.periods',
      'tenant.reports',
    ];
    const params = { include };
    const additionalParams = this.getAdditionalQueryParams(isDraft);
    return this.tarlV3Jsonapi
      .getResource<ListItem>(path, params, additionalParams)
      .pipe(
        map(item => {
          if (!item.list || !item.list.id) {
            throw new Error(
              'Single items must contain a reference to their list',
            );
          }
          return item;
        }),
      );
  }

  /**
   * Get a page of items for a reading list from the API.
   *
   * @param {string} listId - The ID of the list to retrieve.
   * @param {boolean=} isDraft - Whether or not to fetch from the draft list
   * @param {number=} offset - The starting index of items within the list
   * @param {number=} totalPages - Total number of item pages
   * @param {number=} limit - The maximum number of items to retrieve
   * @param {string=} citationStyle - The citation style to fetch
   * @param {string[]=} include - The JSON:API includes to request
   * @return {Observable<PageableItems>} - A page of list items.
   **/
  getReadingListItems(
    listId: string,
    isDraft = false,
    offset = 0,
    totalPages = 0,
    limit = LIST_ITEMS_PAGE_SIZE,
    citationStyle: string = undefined,
    include: string[] = LIST_ITEM_INCLUDES,
  ): Observable<PageableItems> {
    const path = this.tarlV3Jsonapi.path('/lists/{listId}/items', { listId });
    const params = { include, limit, offset };
    const additionalParams = this.getAdditionalQueryParams(
      isDraft,
      citationStyle,
    );
    const convertPageWithOffsetAndTotal = pageableItem =>
      this.convertPageOfItemsToPageableItems(pageableItem, totalPages, offset);
    return this.tarlV3Jsonapi
      .getPageOfResources<ListItem>(path, params, additionalParams)
      .pipe(
        map(convertPageWithOffsetAndTotal),
        catchError(error =>
          observableThrowError(getPageableItemsErrorWithOffset(error, offset)),
        ),
      );
  }

  /**
   * Get a page of items for a section from the API.
   *
   * @param {string} sectionId - The ID of the section to retrieve.
   * @param {boolean=} isDraft - Whether or not to fetch from the draft list
   * @param {number=} offset - The starting index of items within the list
   * @param {number=} totalPages - Total number of item pages
   * @param {number=} limit - The maximum number of items to retrieve
   * @param {string=} citationStyle - The citation style to fetch
   * @param {string[]=} include - The JSON:API includes to request
   * @return {Observable<PageableItems>} - A page of list items.
   **/
  getSectionItems(
    sectionId: string,
    isDraft = false,
    offset = 0,
    totalPages = 0,
    limit = LIST_ITEMS_PAGE_SIZE,
    citationStyle: string = undefined,
    include: string[] = SECTION_ITEM_INCLUDES,
  ): Observable<PageableItems> {
    const path = this.tarlV3Jsonapi.path('/sections/{sectionId}/items', {
      sectionId,
    });
    const params = { include, limit, offset };
    const additionalParams = this.getAdditionalQueryParams(
      isDraft,
      citationStyle,
    );
    const convertPageWithOffsetAndTotal = pageableItem =>
      this.convertPageOfItemsToPageableItems(pageableItem, totalPages, offset);
    return this.tarlV3Jsonapi
      .getPageOfResources<ListItem>(path, params, additionalParams)
      .pipe(
        map(convertPageWithOffsetAndTotal),
        catchError(error =>
          observableThrowError(getPageableItemsErrorWithOffset(error, offset)),
        ),
      );
  }

  convertPageOfItemsToPageableItems(
    pageOfResources: PageOf<ListItem>,
    totalPages: number,
    pageOffset: number,
  ): PageableItems {
    const { data: items, links, meta } = pageOfResources;
    return { items, totalPages, pageOffset, links, meta };
  }

  /**
   * Requests all pages of items for a given reading list, returning as an observable that accumulates all items.
   *
   * @param {string} listId - the ID of the list to fetch
   * @param {number} totalItems - the total number of items to fetch for the list
   * @param {boolean=} isDraft - whether or not to fetch from the draft list
   * @param {number=} pageSize - the number of items fetched per page
   * @return {Observable<PageableItems>} - an observable that accumulates all pages of items for a given list.
   **/
  getReadingListItemsInPages(
    listId: string,
    totalItems: number,
    isDraft = false,
    pageSize = LIST_ITEMS_PAGE_SIZE,
  ): Observable<PageableItems> {
    // lists without items should immediately return an empty pageable
    if (!totalItems || totalItems === 0) {
      return observableOf(PAGEABLE_EMPTY_ITEMS);
    }

    const totalPages = getTotalPages(totalItems, pageSize);

    return concat(
      // get the first page first and concat with all others.
      this.getReadingListItems(listId, isDraft, 0, totalPages, pageSize),
      // fetch all pages in parallel (skipping the first one) and emit each page after it is retrieved
      observableFrom(getPageOffsets(totalItems, pageSize)).pipe(
        skip(1),
        mergeMap(
          (pageOffset: number) =>
            this.getReadingListItems(
              listId,
              isDraft,
              pageOffset,
              totalPages,
              pageSize,
            ),
          CONCURRENCY_LIMIT,
        ),
      ),
    );
  }

  /**
   * Requests all pages of items for a given section, returning as an observable that accumulates all items.
   *
   * @param {string} sectionId - the ID of the section to fetch
   * @param {number} total - the total number of items to fetch for the section
   * @param {boolean=} isDraft - whether or not to fetch from the draft list
   * @param {number=} pageSize - the number of items fetched per page
   * @return {Observable<PageableItems>} - an observable that accumulates all pages of items for a given section.
   **/
  getSectionItemsInPages(
    sectionId: string,
    total: number,
    isDraft = false,
    pageSize = LIST_ITEMS_PAGE_SIZE,
  ): Observable<PageableItems> {
    // section without items should immediately return an empty pageable
    if (!total || total === 0) {
      return observableOf(PAGEABLE_EMPTY_ITEMS);
    }

    // fetch all pages in parallel and emit each page after it is retrieved
    return observableFrom(getPageOffsets(total, pageSize)).pipe(
      mergeMap(
        (pageOffset: number) =>
          this.getSectionItems(sectionId, isDraft, pageOffset, pageSize),
        CONCURRENCY_LIMIT,
      ),
    );
  }

  /**
   * Fetches all list item citation values for a given citation style and list, keyed by item ID.
   *
   * Note that this traverses all pages of the list in parallel.
   *
   * @param {string} listId - the ID of the list to fetch
   * @param {boolean} isDraft - whether or not to fetch from the draft list
   * @param {string} citationStyle - the citation style to fetch
   * @param {number} totalItems - the total number of items to fetch for the list
   * @param {number=} pageSize - the number of items fetched per page
   * @return {Observable<CitationsByItem>} - an observable of all citation values for a given list, keyed by item ID
   **/
  getReadingListItemCitationsByItemId(
    listId: string,
    isDraft: boolean,
    citationStyle: string,
    totalItems: number,
    pageSize = LIST_ITEMS_PAGE_SIZE,
  ): Observable<CitationsByItem> {
    // lists without items have no citations, so immediately return an empty map
    if (!totalItems || totalItems === 0) {
      return observableOf(Map());
    }

    const totalPages = getTotalPages(totalItems / pageSize);

    // lists with items should parallel-fetch citation content
    return observableFrom(getPageOffsets(totalItems, pageSize)).pipe(
      mergeMap(
        (pageOffset: number) =>
          this.getReadingListItems(
            listId,
            isDraft,
            pageOffset,
            totalPages,
            pageSize,
            citationStyle,
            [],
          ),
        CONCURRENCY_LIMIT,
      ),
      scan<PageableItems, Map<string, Citation>>(
        this.reduceCitationsByItem.bind(this),
        Map<string, Citation>(),
      ),
    );
  }

  /**
   * Fetches all section item citation values for a given citation style and section, keyed by item ID.
   *
   * Note that this traverses all pages of the list in parallel.
   *
   * @param {string} sectionId - the ID of the section to fetch
   * @param {boolean} isDraft - whether or not to fetch from the draft list
   * @param {string} citationStyle - the citation style to fetch
   * @param {number} total - the total number of items to fetch for the section
   * @param {number=} pageSize - the number of items fetched per page
   * @return {Observable<CitationsByItem>} - an observable of all citation values for a given section, keyed by item ID
   **/
  getSectionItemCitationsByItemId(
    sectionId: string,
    isDraft: boolean,
    citationStyle: string,
    totalItems: number,
    pageSize = LIST_ITEMS_PAGE_SIZE,
  ): Observable<CitationsByItem> {
    // sections without items have no citations, so immediately return an empty map
    if (!totalItems || totalItems === 0) {
      return observableOf(Map());
    }

    const totalPages = getTotalPages(totalItems / pageSize);

    // lists with items should parallel-fetch citation content
    return observableFrom(getPageOffsets(totalItems, pageSize)).pipe(
      mergeMap(
        (pageOffset: number) =>
          this.getSectionItems(
            sectionId,
            isDraft,
            pageOffset,
            totalPages,
            pageSize,
            citationStyle,
            [],
          ),
        CONCURRENCY_LIMIT,
      ),
      scan<PageableItems, Map<string, Citation>>(
        this.reduceCitationsByItem.bind(this),
        Map<string, Citation>(),
      ),
    );
  }

  /**
   * Reducer function for accumulating an object of citations, keyed by item ID, for a page of items.
   *
   * @param {CitationsByItem} citationsByItem - citations by item
   * @param {PageableItems} page - a page of items
   * @return {CitationsByItem} - citations by item, after processing page
   **/
  reduceCitationsByItem(
    citationsByItem: CitationsByItem,
    page: PageableItems,
  ): CitationsByItem {
    return page.items.reduce(
      (soFar, item) => soFar.merge({ [item.id]: item.citation }),
      citationsByItem,
    );
  }

  /**
   * Archive the list
   *
   * @param {string} listId - The ID of the list to archive.
   * @return {Observable<boolean>} - An observable of the success status of the API call
   */
  archiveList(listId: string): Observable<boolean> {
    const path = this.tarlV3Jsonapi.path('/lists/{listId}/archive', { listId });
    return this.tarlV3Api.post(path, {}).pipe(map(response => response.ok));
  }

  /**
   * Delete the list
   *
   * @param {string} listId - The ID of the list to delete.
   * @return {Observable<boolean>} - An observable of the success status of the API call
   */
  deleteList(listId: string): Observable<boolean> {
    const path = this.tarlV3Jsonapi.path('/lists/{listId}', { listId });
    return this.tarlV3Jsonapi.delete(path).pipe(map(response => response.ok));
  }

  createNewList(payload: CreateListPayload): Observable<ReadingListDetails> {
    const path = this.tarlV3Jsonapi.path('/draft_lists', {});
    const listWithStudentNumbers = ReadingListDetails.create({
      ...payload.list,
      student_numbers: payload.hierarchy.listStudentNumbers,
    });
    const listJsonAPI = toJsonApi(listWithStudentNumbers);
    const hierarchyData = (payload.hierarchy.nodesAndOverrides || []).map(
      getRelationshipFromNodeAndOverride,
    );
    const body = {
      data: {
        ...listJsonAPI,
        ...(hierarchyData.length > 0 && {
          relationships: {
            ...listJsonAPI.relationships,
            nodes: { data: hierarchyData },
          },
        }),
      },
    };

    return this.tarlV3Jsonapi
      .postRawJsonapiResponse(path, body)
      .pipe(map(response => deserialiseOne<ReadingListDetails>(response)));
  }

  /**
   * Fetch a single list section.
   *
   * @param  {string} sectionId - the ID for the section to fetch
   * @param  {Boolean} isDraft - whether to fetch the draft section
   * @return {Observable<Section>} The section, deserialised from JSON:API
   */
  getSection(sectionId: string, isDraft = false): Observable<Section> {
    const path = this.tarlV3Jsonapi.path('/sections/{sectionId}', {
      sectionId,
    });

    // JSON:API includes to request list structure be included in response
    const include = [
      'list',
      'rolled_over_to',
      'rolled_over_from',
      'sections_recursively',
      'tenant.bookstores',
      'tenant.citation_styles',
      'tenant.importances',
      'tenant.periods',
      'tenant.reports',
    ];

    const params = { include };
    const additionalParams = this.getAdditionalQueryParams(isDraft);
    return this.tarlV3Jsonapi.getResource<Section>(
      path,
      params,
      additionalParams,
    );
  }

  getAdditionalQueryParams(
    isDraft: boolean = false,
    style: string = undefined,
  ): { [key: string]: string } {
    const params = {};
    if (isDraft) {
      params['draft'] = '1';
    }
    if (style) {
      params['style'] = style;
    }
    return params;
  }
}
